import { useEffect, useMemo, useState } from 'react';
import Button from '@/components/base/Button';
import { formatMoney } from '@/utils/format';
import type { BulkCandidate, BulkChannel } from '@/types/portal';
import { PLATFORM } from '@/config/platform';

export interface CampaignOption {
  id: string;
  name: string;
  currency: string;
  /** Creators ready to pay. */
  count: number;
  /** Hired creators not payable yet. */
  waiting: number;
  /** Creators on the campaign not hired yet. */
  notHired: number;
}

/** What each mark in the creator list means; shown under it. */
const KEY = [
  { icon: 'ri-checkbox-line', tone: 'text-primary-600', label: 'Ready to pay: tick to add' },
  { icon: 'ri-check-line', tone: 'text-accent-600', label: 'Already in this batch' },
  { icon: 'ri-time-line', tone: 'text-secondary-600', label: 'Hired, not payable yet: content still needs approving, or it is already paid' },
  { icon: 'ri-user-line', tone: 'text-foreground-400', label: 'Not hired yet: invited, negotiating or waiting on a contract' },
];

function optionLabel(campaign: CampaignOption) {
  if (campaign.count + campaign.waiting + campaign.notHired === 0) return `${campaign.name} · no creators yet`;
  if (campaign.count + campaign.waiting === 0) return `${campaign.name} · nobody hired yet`;
  return `${campaign.name} · ${campaign.count} ready${campaign.waiting > 0 ? ` · ${campaign.waiting} not payable yet` : ''}`;
}

interface CampaignPullPanelProps {
  campaigns: CampaignOption[];
  channel: BulkChannel;
  /** Payable creators for the wallet's currency and channel, added or not. */
  ready: BulkCandidate[];
  /** Creators on a campaign who can't be paid yet, with the reason (`notHired` for ones not hired yet). */
  waiting: BulkCandidate[];
  /** Ids already in the batch. */
  addedIds: string[];
  onAdd: (candidates: BulkCandidate[]) => void;
}

/** Pick a campaign, see every hired creator on it, tick the payable ones to add. */
export default function CampaignPullPanel({
  campaigns,
  channel,
  ready,
  waiting,
  addedIds,
  onAdd,
}: CampaignPullPanelProps) {
  const [campaignId, setCampaignId] = useState('');
  const [ticked, setTicked] = useState<string[]>([]);

  const readyHere = useMemo(
    () => ready.filter((candidate) => candidate.campaign === campaignId && !addedIds.includes(candidate.id)),
    [ready, campaignId, addedIds],
  );
  const addedHere = useMemo(
    () => ready.filter((candidate) => candidate.campaign === campaignId && addedIds.includes(candidate.id)),
    [ready, campaignId, addedIds],
  );
  // Hired-but-waiting first, then creators not hired yet.
  const waitingHere = useMemo(
    () =>
      waiting
        .filter((candidate) => candidate.campaign === campaignId)
        .sort((a, b) => Number(Boolean(a.notHired)) - Number(Boolean(b.notHired))),
    [waiting, campaignId],
  );

  // Every payable creator starts ticked whenever the campaign (or who's left to add) changes.
  useEffect(() => {
    setTicked(readyHere.map((candidate) => candidate.id));
  }, [readyHere]);

  const toggle = (id: string) =>
    setTicked((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));

  const add = () => onAdd(readyHere.filter((candidate) => ticked.includes(candidate.id)));

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="pull-campaign" className="text-xs font-label font-medium text-foreground-600">
          Campaign <span className="text-primary-600">*</span>
        </label>
        <select
          id="pull-campaign"
          value={campaignId}
          onChange={(event) => setCampaignId(event.target.value)}
          className="mt-1.5 h-10 w-full max-w-md rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 cursor-pointer focus:border-primary-400 focus:outline-none"
        >
          <option value="">Select a campaign...</option>
          {campaigns.map((campaign) => (
            <option key={campaign.id} value={campaign.id}>
              {optionLabel(campaign)}
            </option>
          ))}
        </select>
      </div>

      {campaignId ? (
        <div className="overflow-hidden rounded-md border border-background-200 animate-fade-in">
          {readyHere.length === 0 && addedHere.length === 0 && waitingHere.length === 0 ? (
            <p className="px-4 py-3 text-sm text-foreground-600">No creators on this campaign yet.</p>
          ) : (
            <ul className="max-h-80 divide-y divide-background-100 overflow-y-auto">
              {readyHere.map((candidate) => (
                <li key={candidate.id}>
                  <label className="flex cursor-pointer items-center gap-3 px-4 py-2.5 hover:bg-background-100">
                    <input
                      type="checkbox"
                      checked={ticked.includes(candidate.id)}
                      onChange={() => toggle(candidate.id)}
                      className="h-4 w-4 cursor-pointer accent-primary-600"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-foreground-900">{candidate.name}</span>
                      <span className="block truncate text-xs text-foreground-500">
                        {candidate.blocked ?? `${candidate.walletType} wallet · ${candidate.walletId}`}
                      </span>
                    </span>
                    <span className="shrink-0 text-xs font-medium text-foreground-700">
                      {formatMoney(candidate.suggestedAmount, candidate.currency)}
                    </span>
                  </label>
                </li>
              ))}
              {addedHere.map((candidate) => (
                <li key={candidate.id} className="flex items-center gap-3 px-4 py-2.5 opacity-70">
                  <i className="ri-check-line text-base text-accent-600" />
                  <span className="min-w-0 flex-1 truncate text-sm text-foreground-900">{candidate.name}</span>
                  <span className="shrink-0 text-xs text-foreground-500">Added</span>
                </li>
              ))}
              {waitingHere.map((candidate) => (
                <li
                  key={candidate.id}
                  className="flex items-center gap-3 px-4 py-2.5 opacity-60"
                  title={`Can't be paid yet: ${candidate.waiting}`}
                >
                  <i
                    className={`text-base ${candidate.notHired ? 'ri-user-line text-foreground-400' : 'ri-time-line text-secondary-600'}`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-foreground-900">{candidate.name}</span>
                    <span className="block truncate text-xs text-foreground-500">{candidate.waiting}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-background-200 px-4 py-2.5">
            <p className="text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
              Key
            </p>
            <ul className="mt-1.5 grid gap-x-4 gap-y-1 sm:grid-cols-2">
              {KEY.map((item) => (
                <li key={item.icon} className="flex items-start gap-1.5 text-xs text-foreground-600">
                  <i className={`${item.icon} ${item.tone} mt-px text-sm`} />
                  {item.label}
                </li>
              ))}
            </ul>
            <p className="mt-1.5 text-xs text-foreground-500">
              Greyed-out creators can't be added. A creator becomes payable once all their content
              is approved.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-background-200 bg-background-100 px-4 py-2.5">
            <span className="text-xs text-foreground-600">
              {readyHere.length > 0
                ? `${ticked.length} of ${readyHere.length} ready to add`
                : 'Nobody on this campaign is ready to pay yet.'}
            </span>
            <Button size="sm" icon="ri-add-line" onClick={add} disabled={ticked.length === 0}>
              Add selected{ticked.length > 0 ? ` (${ticked.length})` : ''}
            </Button>
          </div>
        </div>
      ) : null}

      <div className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3.5 py-3 text-xs text-secondary-900">
        <i className="ri-hand-coin-line mt-0.5 text-sm" />
        <span>
          Pulling from a campaign checks the early payout logic. Where a creator already borrowed
          against their invoice, only the remaining balance is sent to them and the borrowed amount
          is auto-swept to the {PLATFORM.name} Pool Account.
        </span>
      </div>

      {campaigns.length === 0 ? (
        <p className="text-xs text-foreground-500">
          No campaigns have {channel === 'mobile' ? 'Mobile Money' : PLATFORM.walletName} creators for
          the selected wallet currency.
        </p>
      ) : null}
    </div>
  );
}
